import { ApplicationConfig, importProvidersFrom, LOCALE_ID, Injector, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideHttpClient, withInterceptors, HttpClient, HttpBackend } from '@angular/common/http';
import { provideRouter, TitleStrategy, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { TranslateModule, TranslateLoader, TranslateCompiler } from '@ngx-translate/core';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { SafeTranslateMessageFormatCompiler } from './core/strategies/safe-translate-messageformat.compiler.service';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { CustomTitleStrategy } from './core/strategies/title.strategy';
import { routes } from './app.routes';
import { customInterceptor } from './core/interceptors/http-interceptor';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { ToastrModule } from 'ngx-toastr';
import { DuiNotificationsComponent } from './shared/components/dui-notifications/dui-notifications.component';
import { ICON_SETTINGS, IconSettingsService } from '@progress/kendo-angular-icons';
import { KendoIconsService } from './shared/services/kendo-icons.service';
import { createCustomElement } from '@angular/elements';
import { MentionComponent } from './shared/components/mention/mention.component';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';

import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import localeEsMx from '@angular/common/locales/es-MX';
import localeEn from '@angular/common/locales/en';
import localeCa from '@angular/common/locales/ca';
import localeEu from '@angular/common/locales/eu';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import localePt from '@angular/common/locales/pt';
import localeRo from '@angular/common/locales/ro';
import localeSk from '@angular/common/locales/sk';
import localeZhHans from '@angular/common/locales/zh-Hans';

registerLocaleData(localeEs);
registerLocaleData(localeEn);
registerLocaleData(localeEsMx);
registerLocaleData(localeCa);
registerLocaleData(localeEu);
registerLocaleData(localeFr);
registerLocaleData(localeIt);
registerLocaleData(localePt);
registerLocaleData(localeRo);
registerLocaleData(localeSk);
registerLocaleData(localeZhHans);

/**
 * Translation loader factory that avoids the use of interceptors by directly using HttpBackend,
 * thus breaking the circular dependency error with HttpClient.
 */
export function HttpLoaderFactory(httpBackend: HttpBackend): TranslateLoader {
    const http = new HttpClient(httpBackend);
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } }, // <-- Evita que los popups de Kendo queden ocultos tras diálogos de Material
        // We set Spanish as the default language for Angular pipes (Date, Currency, etc.)
        { provide: LOCALE_ID, useValue: 'es' },
        provideAnimationsAsync(),
        provideHttpClient(withInterceptors([customInterceptor])),
        importProvidersFrom(
            // Configuring the translation module using HttpBackend to avoid circular dependencies
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: HttpLoaderFactory,
                    deps: [HttpBackend] // Do NOT use HttpClient here!
                },
                compiler: {
                    provide: TranslateCompiler,
                    useClass: SafeTranslateMessageFormatCompiler
                },
                defaultLanguage: 'es'
            }),
            ToastrModule.forRoot({
                toastComponent: DuiNotificationsComponent,
                maxOpened: 3,
                autoDismiss: true,
                enableHtml: true
            })
        ),
        // Route configuration
        provideRouter(
            routes,
            withComponentInputBinding(),
            withRouterConfig({
                canceledNavigationResolution: 'computed',
                paramsInheritanceStrategy: 'always'
            })
        ),
        // Custom strategy for translate page titles
        { provide: TitleStrategy, useClass: CustomTitleStrategy },
        // Kendo UI Font Icons Service
        { provide: ICON_SETTINGS, useValue: { type: 'font' } },
        { provide: IconSettingsService, useClass: KendoIconsService },
        provideAppInitializer(() => {
            const injector = inject(Injector);
            const mentionElement = createCustomElement(MentionComponent, { injector });
            if (!customElements.get('mention-element')) {
                customElements.define('mention-element', mentionElement);
            }
        })
    ]
};
